import React from "react";
import LegalPageLayout from "@user/components/LegalPageLayout";
import { SUPPORT_EMAIL } from "@/config/support";

/**
 * Terms & Conditions — governs use of Advoset's platform, marketplace,
 * consultations, and payments. Written to be readable by an ordinary user
 * while satisfying the disclosure requirements Cashfree and Indian
 * consumer-protection law expect (jurisdiction, arbitration, service
 * description, fees).
 */
export default function Terms() {
  return (
    <LegalPageLayout title="Terms & Conditions" lastUpdated="7 February 2026">
      <p>
        These Terms &amp; Conditions (&quot;Terms&quot;) govern your access to and
        use of the Advoset platform, including our website, mobile applications,
        and any related services (collectively, the &quot;Service&quot;) operated by
        Advoset Technologies Private Limited (&quot;Advoset&quot;, &quot;we&quot;,
        &quot;us&quot;, or &quot;our&quot;). By using the Service, you agree to be
        bound by these Terms.
      </p>

      <h2>1. Who we are</h2>
      <p>
        Advoset is a technology platform that connects users seeking legal help
        with independent, verified advocates enrolled with a State Bar Council
        in India. Advoset itself does not practise law and does not provide
        legal advice. All legal advice you receive is provided by the
        independent lawyer you consult through the platform.
      </p>

      <h2>2. Eligibility</h2>
      <p>
        You must be at least 18 years old and legally competent to enter into a
        binding contract under the Indian Contract Act, 1872. By registering, you
        represent that the information you provide is accurate and current.
      </p>

      <h2>3. Services offered</h2>
      <ul>
        <li>
          <strong>AI Legal Assistant</strong> — a general-purpose AI tool that
          provides information about Indian law. Its outputs are informational
          only and are not legal advice.
        </li>
        <li>
          <strong>Lawyer Marketplace</strong> — a directory of verified lawyers
          you can search, filter, and contact.
        </li>
        <li>
          <strong>Consultations</strong> — you can book paid consultations with
          lawyers over chat, audio, or video. The engagement, scope of work, and
          professional relationship exist between you and the lawyer directly.
          Advoset is a facilitator only.
        </li>
        <li>
          <strong>Knowledge Hub</strong> — articles and guides on Indian law.
        </li>
      </ul>

      <h2>4. Payments</h2>
      <p>
        Consultation fees are displayed before you confirm a booking and are
        collected by Advoset on the lawyer&apos;s behalf through a
        Reserve-Bank-of-India-authorised payment aggregator (Cashfree Payments).
        Advoset charges a platform commission on each successful consultation.
        Applicable taxes (including GST where relevant) will be added at
        checkout. All amounts are in Indian Rupees (INR) unless stated otherwise.
      </p>

      <h2>5. Refunds &amp; cancellations</h2>
      <p>
        Refunds and cancellations are governed by our{" "}
        <a href="/refund-policy">Refund &amp; Cancellation Policy</a>, which
        forms part of these Terms.
      </p>

      <h2>6. Conduct of consultations</h2>
      <ul>
        <li>
          Consultations are strictly for lawful purposes. You may not use the
          Service to plan, further, or conceal any offence.
        </li>
        <li>
          Recording of consultations is prohibited unless both parties provide
          written consent.
        </li>
        <li>
          Communications on the platform are governed by the Advocates Act,
          1961, the Bar Council of India Rules, and the confidentiality
          obligations that flow between an advocate and their client.
        </li>
      </ul>

      <h2>7. Content &amp; intellectual property</h2>
      <p>
        All platform content (including software, design, articles, and AI
        prompts) is owned by Advoset or its licensors. You may not copy,
        redistribute, or exploit any portion of the Service commercially without
        our written consent.
      </p>

      <h2>8. Disclaimers</h2>
      <p>
        The Service is provided on an &quot;as is&quot; basis. To the maximum
        extent permitted by law, Advoset disclaims all warranties, express or
        implied. AI-generated outputs are not a substitute for consultation
        with a qualified advocate.
      </p>

      <h2>9. Limitation of liability</h2>
      <p>
        To the maximum extent permitted by law, Advoset&apos;s aggregate
        liability arising out of or in connection with these Terms is limited
        to the platform fees paid by you to Advoset in the three (3) months
        preceding the event giving rise to the claim. Advoset is not liable
        for any legal advice provided by lawyers on the platform.
      </p>

      <h2>10. Termination</h2>
      <p>
        We may suspend or terminate your access if you breach these Terms, or
        if we are required to do so by law. Provisions relating to intellectual
        property, disclaimers, limitations of liability, indemnities, and
        dispute resolution will survive termination.
      </p>

      <h2>11. Governing law &amp; jurisdiction</h2>
      <p>
        These Terms are governed by the laws of India. Subject to the
        arbitration provision below, the courts at Bengaluru, Karnataka, will
        have exclusive jurisdiction.
      </p>

      <h2>12. Dispute resolution</h2>
      <p>
        Any dispute arising out of or in connection with these Terms shall
        first be attempted to be resolved amicably by writing to{" "}
        <a href="mailto:grievance@advoset.in">grievance@advoset.in</a>. If
        unresolved within 30 days, the dispute shall be referred to arbitration
        by a sole arbitrator under the Arbitration and Conciliation Act, 1996.
        The seat of arbitration shall be Bengaluru; the language shall be
        English.
      </p>

      <h2>13. Changes to these Terms</h2>
      <p>
        We may update these Terms from time to time. Material changes will be
        notified through the platform or by email. Continued use after the
        effective date constitutes acceptance.
      </p>

      <h2>14. Contact</h2>
      <p>
        Questions about these Terms? Write to{" "}
        <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> or see our{" "}
        <a href="/contact">Contact Us</a> page.
      </p>
    </LegalPageLayout>
  );
}
